// src/pages/ProblemBankAnalystDashboard.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { demoDataService } from '@/lib/demoDataService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  FileText,
  Bell,
  Database,
  Plus,
  Loader2,
  Search,
  Eye,
  Building2,
  MapPin,
  RefreshCw,
  CheckCircle,
  Inbox,
  TrendingUp,
  UserCheck,
  Wifi,
  WifiOff
} from 'lucide-react';

const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
};

export default function ProblemBankAnalystDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [pendingSubmissions, setPendingSubmissions] = useState<any[]>([]);
  const [allProblems, setAllProblems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  
  const subscriptionsRef = useRef<any[]>([]);
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const loadDataRef = useRef<() => Promise<void>>();
  const isInitialLoadRef = useRef(true);

  const isDemoUser = demoDataService.isDemoAccount(user?.email);
  const isProblemBankAnalyst = user?.analyst_type === 'problem_bank' && user?.role !== 'senior_analyst';

  const loadData = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      let problems = [];
      let submissions = [];

      if (isDemoUser && demoDataService.isDemoModeActive(user?.id, user?.email)) {
        console.log('📊 Demo mode active for this user - loading from localStorage');
        problems = demoDataService.getDemoProblems(user?.id, user?.email);
        submissions = demoDataService.getDemoSubmissions(user?.id, user?.email);
      } else {
        console.log('📊 Loading real data from Supabase');
        
        const userId = user?.id;
        
        const { data: submissionsData, error: submissionsError } = await supabase
          .from('external_submissions')
          .select('*')
          .in('status', ['pending', 'under_review'])
          .order('submitted_at', { ascending: false });
        
        if (!submissionsError) {
          submissions = (submissionsData || []).map(sub => ({
            id: sub.id,
            problem_title: sub.problem_title || 'Untitled Problem',
            problem_description: sub.problem_description || '',
            company_name: sub.company_name || sub.contact_name || 'Individual Submission',
            contact_name: sub.contact_name || '',
            contact_email: sub.contact_email || '',
            contact_phone: sub.contact_phone || '',
            estimated_impact: sub.estimated_impact || '',
            urgency: sub.urgency || 'medium',
            status: sub.status || 'pending',
            submitted_at: sub.submitted_at,
            created_at: sub.created_at,
            converted_to_problem_id: sub.converted_to_problem_id,
            industry_sector: sub.industry_sector,
            category: sub.category,
            quantification: sub.quantification,
            root_cause: sub.root_cause,
            solution_potential: sub.solution_potential,
            photo_urls: sub.photo_urls,
            data_files: sub.data_files,
            total_annual_cost: sub.total_annual_cost,
            pilot_readiness: sub.pilot_readiness,
            technical_info: sub.technical_info,
            location: sub.location
          }));
          console.log(`📊 Loaded ${submissions.length} pending submissions`);
        }

        let problemsQuery = supabase
          .from('problem_bank')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (isProblemBankAnalyst && userId) {
          console.log('🔍 Filtering by created_by =', userId);
          problemsQuery = problemsQuery.eq('created_by', userId);
        }
        
        const { data: problemsData, error: problemsError } = await problemsQuery;
        
        if (!problemsError) {
          problems = problemsData || [];
          console.log(`📊 Loaded ${problems.length} problems${isProblemBankAnalyst ? ' (filtered by creator)' : ''}`);
        } else {
          console.error('❌ Problems error:', problemsError);
        }
      }

      const mappedProblems = problems.map(problem => ({
        ...problem,
        id: problem.id,
        problemId: problem.problem_id,
        priorityScore: problem.priority_score,
        anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor) : problem.anchor,
        quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification) : problem.quantification,
        verified_by: problem.verified_by,
        verified_at: problem.verified_at
      }));
      
      console.log(`📊 Final mapped problems count: ${mappedProblems.length}`);
      
      setAllProblems(mappedProblems);
      setPendingSubmissions(submissions);
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error('❌ Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
      isInitialLoadRef.current = false;
    }
  }, [user, isDemoUser, isProblemBankAnalyst, toast, isRefreshing]);

  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  const refreshData = useCallback(async () => {
    if (loadDataRef.current && !isRefreshing) {
      await loadDataRef.current();
      toast({
        title: "Data Refreshed",
        description: "All dashboard data has been updated.",
      });
    }
  }, [toast, isRefreshing]);

  const handleViewProblemDetails = (problem: any) => {
    const problemIdentifier = problem.problemId || problem.problem_id;
    
    console.log('🔍 Navigating to problem with ID:', problemIdentifier);
    
    if (!problemIdentifier) {
      toast({
        title: "Error",
        description: "Cannot view problem: Invalid ID",
        variant: "destructive"
      });
      return;
    }
    
    navigate(`/problem-bank/problem/${problemIdentifier}`);
  };

  const handleReviewSubmission = (submission: any) => {
    console.log('🔍 Reviewing submission:', submission.id);
    
    const formattedSubmission = {
      id: submission.id,
      problem_title: submission.problem_title || 'Untitled Problem',
      problem_description: submission.problem_description || '',
      company_name: submission.company_name || submission.contact_name || 'Individual Submission',
      contact_name: submission.contact_name || '',
      contact_email: submission.contact_email || '',
      contact_phone: submission.contact_phone || '',
      estimated_impact: submission.estimated_impact || '',
      urgency: submission.urgency || 'medium',
      status: submission.status || 'pending',
      submitted_at: submission.submitted_at,
      created_at: submission.created_at,
      industry_sector: submission.industry_sector,
      category: submission.category,
      quantification: submission.quantification,
      root_cause: submission.root_cause,
      solution_potential: submission.solution_potential,
      photo_urls: submission.photo_urls,
      data_files: submission.data_files
    };
    
    navigate(`/problem-bank/submissions/review/${submission.id}`, {
      state: { submission: formattedSubmission }
    });
  };

  // FIXED: WebSocket subscriptions - runs ONCE with empty dependency array
  useEffect(() => {
    if (!user) return;

    if (isDemoUser && demoDataService.isDemoModeActive(user?.id, user?.email)) {
      loadData();
      return;
    }

    loadData();

    console.log('🔌 Setting up WebSocket subscriptions for Problem Bank Analyst Dashboard...');

    // Problem Bank subscription
    const problemBankChannel = supabase
      .channel('problem-bank-analyst-problems')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'problem_bank',
          filter: isProblemBankAnalyst && user?.id ? `created_by=eq.${user.id}` : undefined
        },
        (payload) => {
          console.log('📡 Problem bank changed:', payload.eventType);
          if (loadDataRef.current && !isRefreshing) {
            setTimeout(() => loadDataRef.current(), 100);
          }
        }
      )
      .subscribe((status) => {
        console.log('🔌 Problem bank subscription status:', status);
        setIsRealtimeConnected(status === 'SUBSCRIBED');
      });

    // Submissions subscription
    const submissionsChannel = supabase
      .channel('problem-bank-analyst-submissions')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'external_submissions'
        },
        (payload) => {
          console.log('📡 Submissions changed:', payload.eventType);
          if (loadDataRef.current && !isRefreshing) {
            setTimeout(() => loadDataRef.current(), 100);
          }
        }
      )
      .subscribe();

    subscriptionsRef.current = [problemBankChannel, submissionsChannel];

    // Set up polling (every 30 seconds as fallback)
    console.log('🚀 Setting up polling for Problem Bank Analyst Dashboard (every 30 seconds)...');
    pollingInterval.current = setInterval(() => {
      if (loadDataRef.current && !isRefreshing && !isInitialLoadRef.current) {
        console.log('🔄 Polling: Refreshing dashboard data...');
        loadDataRef.current();
      }
    }, 30000);

    // Refresh on tab focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Tab focused - refreshing dashboard');
        if (loadDataRef.current && !isRefreshing) {
          loadDataRef.current();
        }
      }
    };
    
    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      console.log('🧹 Cleaning up subscriptions and polling');
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      subscriptionsRef.current.forEach(channel => {
        try {
          supabase.removeChannel(channel);
        } catch (e) {
          console.warn('Error removing channel:', e);
        }
      });
      subscriptionsRef.current = [];
    };
  }, []); // EMPTY dependency array - runs only once when component mounts

  const handleLoadDemoData = async () => {
    if (!isDemoUser) {
      toast({
        title: "Not Available",
        description: "Demo data is only available for demo accounts.",
        variant: "destructive"
      });
      return;
    }

    setLoadingDemo(true);
    try {
      const result = await demoDataService.loadDemoData(user?.id, user?.email);
      if (result.success) {
        toast({
          title: "✅ Demo Data Loaded",
          description: `Loaded ${result.data?.totalAnalystProblems || 0} sample problems, ${result.data?.totalExternalSubmissions || 0} external submissions.`,
        });
        await loadData();
      } else {
        toast({
          title: "❌ Error",
          description: result.error || "Failed to load demo data",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Demo data error:', error);
      toast({
        title: "Error",
        description: "Failed to load demo data",
        variant: "destructive"
      });
    } finally {
      setLoadingDemo(false);
    }
  };

  const handleClearDemoData = async () => {
    if (!isDemoUser) return;
    
    const result = await demoDataService.clearDemoData(user?.id, user?.email);
    if (result.success) {
      toast({
        title: "✅ Demo Data Cleared",
        description: "Demo data has been cleared. Reloading real data...",
      });
      await loadData();
    }
  };

  const filteredProblems = allProblems.filter(problem => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      problem.title?.toLowerCase().includes(query) ||
      problem.problemId?.toLowerCase().includes(query) ||
      problem.anchor?.name?.toLowerCase().includes(query)
    );
  });

  const stats = {
    totalProblems: allProblems.length,
    pendingSubmissions: pendingSubmissions.length,
    totalLoss: allProblems.reduce((sum, p) => sum + (p.quantification?.totalLossPerYear || 0), 0)
  };

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Problem Bank Analyst Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Review external submissions and manage problems in the bank
            {isDemoUser && demoDataService.isDemoModeActive(user?.id, user?.email) && (
              <span className="ml-2 text-purple-600 font-medium">(Demo Mode)</span>
            )}
            {isProblemBankAnalyst && !isDemoUser && (
              <span className="ml-2 text-blue-600 font-medium">(Your Problems Only)</span>
            )}
          </p>
          <div className="flex items-center gap-3 mt-2">
            <p className="text-xs text-muted-foreground">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
            <div className="flex items-center gap-1">
              {isRealtimeConnected ? (
                <>
                  <Wifi className="h-3 w-3 text-green-500 animate-pulse" />
                  <span className="text-xs text-green-600">Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3 text-red-500" />
                  <span className="text-xs text-red-600">Reconnecting...</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {isDemoUser && (
            <>
              <Button 
                variant="outline" 
                className="border-purple-500 text-purple-600 hover:bg-purple-50" 
                onClick={handleLoadDemoData} 
                disabled={loadingDemo}
              >
                {loadingDemo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                Load Demo Data
              </Button>
              {demoDataService.isDemoModeActive(user?.id, user?.email) && (
                <Button 
                  variant="outline" 
                  className="border-red-500 text-red-600 hover:bg-red-50" 
                  onClick={handleClearDemoData}
                >
                  Clear Demo Data
                </Button>
              )}
            </>
          )}
          <Button variant="default" onClick={() => navigate('/problem-bank/new')} className="bg-green-600 hover:bg-green-700">
            <Plus className="h-4 w-4 mr-2" /> New Problem
          </Button>
          <Button variant="outline" onClick={() => navigate('/problem-bank')}>
            <Database className="h-4 w-4 mr-2" /> Problem Bank
          </Button>
          <Button variant="outline" onClick={refreshData} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard title="Total Problems" value={stats.totalProblems.toString()} description="Problems under your management" icon={Database} color="bg-blue-500" />
        <StatCard title="Pending Submissions" value={stats.pendingSubmissions.toString()} description="Awaiting your review" icon={Inbox} color="bg-yellow-500" />
        <StatCard title="Total Estimated Loss" value={formatCurrency(stats.totalLoss)} description="Across all your problems" icon={TrendingUp} color="bg-red-500" />
      </div>

      <Tabs defaultValue="all-problems" className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="all-problems">All Problems</TabsTrigger>
          <TabsTrigger value="submissions">
            Pending Submissions
            {stats.pendingSubmissions > 0 && (
              <Badge className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5">
                {stats.pendingSubmissions}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all-problems" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" />All Problems</CardTitle>
                <CardDescription>
                  {isProblemBankAnalyst 
                    ? `Problems you have created (${stats.totalProblems} total)`
                    : `All problems in the problem bank (${stats.totalProblems} total)`}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search problems..." 
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)} 
                    className="pl-9" 
                  />
                </div>
                <Button variant="outline" size="sm" onClick={refreshData} disabled={isRefreshing}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
                  <p className="text-muted-foreground mt-2">Loading problems...</p>
                </div>
              ) : filteredProblems.length === 0 ? (
                <div className="text-center py-12">
                  <Database className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">
                    {isProblemBankAnalyst 
                      ? "You haven't created any problems yet"
                      : "No problems found"}
                  </p>
                  {isDemoUser && !demoDataService.isDemoModeActive(user?.id, user?.email) && (
                    <Button variant="link" onClick={handleLoadDemoData} className="mt-2">
                      Load demo data
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Problem / Company</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Loss (KES/yr)</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Verified By</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredProblems.map((problem) => (
                        <TableRow key={problem.id}>
                          <TableCell className="font-mono text-xs">{problem.problemId}</TableCell>
                          <TableCell>
                            <p className="font-medium">{problem.title}</p>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Building2 className="h-3 w-3" />
                              <span>{problem.anchor?.name}</span>
                              <MapPin className="h-3 w-3 ml-1" />
                              <span>{problem.anchor?.location}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              problem.status === 'verified' ? 'bg-green-100 text-green-700' :
                              problem.status === 'assigned' ? 'bg-purple-100 text-purple-700' :
                              problem.status === 'solved' ? 'bg-emerald-100 text-emerald-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {problem.status?.toUpperCase() || 'LEAD'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-red-600 font-medium">
                            {formatCurrency(problem.quantification?.totalLossPerYear || 0)}
                          </TableCell>
                          <TableCell>
                            {problem.priorityScore ? (
                              <Badge className={
                                problem.priorityScore.priorityTier === 'critical' ? 'bg-red-100 text-red-700' : 
                                problem.priorityScore.priorityTier === 'high' ? 'bg-orange-100 text-orange-700' : 
                                'bg-yellow-100 text-yellow-700'
                              }>
                                {problem.priorityScore.priorityTier.toUpperCase()}
                              </Badge>
                            ) : (
                              <Badge variant="outline">-</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {problem.verified_by ? (
                              <div>
                                <div className="text-sm font-medium flex items-center gap-1">
                                  <UserCheck className="h-3 w-3 text-green-600" />
                                  {problem.verified_by}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {problem.verified_at ? new Date(problem.verified_at).toLocaleDateString() : ''}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">Not verified</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleViewProblemDetails(problem)}
                            >
                              <Eye className="h-3 w-3 mr-1" /> View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="submissions" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2"><Inbox className="h-5 w-5" />Pending Submissions</CardTitle>
                <CardDescription>External problem submissions awaiting your review ({pendingSubmissions.length})</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={refreshData} disabled={isRefreshing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
                  <p className="text-muted-foreground mt-2">Loading submissions...</p>
                </div>
              ) : pendingSubmissions.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500 opacity-50" />
                  <p className="text-muted-foreground">No pending submissions</p>
                  <p className="text-sm text-muted-foreground mt-1">All caught up!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingSubmissions.map((submission) => (
                    <div key={submission.id} className="p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => handleReviewSubmission(submission)}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold">{submission.problem_title}</h3>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{submission.problem_description}</p>
                          <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
                            <span>From: {submission.company_name || submission.contact_name || 'Individual'}</span>
                            <span>Contact: {submission.contact_email || 'Not provided'}</span>
                            <span>Submitted: {new Date(submission.submitted_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReviewSubmission(submission);
                          }}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Eye className="h-3 w-3 mr-1" /> Review
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/problem-bank/new')}>
              <Plus className="h-6 w-6" /><span>New Problem</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/problem-bank/submissions')}>
              <Inbox className="h-6 w-6" /><span>View Submissions</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/problem-bank')}>
              <Database className="h-6 w-6" /><span>Problem Bank</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/notifications')}>
              <Bell className="h-6 w-6" /><span>Notifications</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ title, value, description, icon: Icon, color }: any) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          </div>
          <div className={`${color} p-3 rounded-full text-white`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}